// Globals
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { FormItem, IFormInstance, showErrorToast, useWatchForm } from '@/components';
import { Input, InputNumber, Radio, Skeleton } from '@/ui';
import {
  StyledForm,
  StyledFormItemsBlock,
  StyledDivider,
  StyledRadioGroup,
  StyledUnitsSelect,
} from './ProductMainInfoFormStyles';
import ProductPhotosField from '../ProductPhotosField/ProductPhotosField';

// Modules
import { LOCALES_FIELD_NAME, LocalesFormFields } from '@/modules/Locales';

// API
import { ProductsAPI } from '@/api';

// Models
import { IProduct } from '@/models/products.model';
import { SupportedLocales } from '@/models/localization.model';

// Helpers
import {
  ProductMainInfoFormFieldsNames,
  TProductMainInfoFormValues,
  getUnitsOptions,
  getPackageQuantityLabel,
  isServiceUnit,
  isIntegerUnit,
  MeasureUnitCode,
  getFieldSourceLabel,
  withFieldSourceLabel,
} from '../../helpers';
import { useProductDetailsValidationRules, useTransformedProductInitialValues } from '../../hooks';

const initialLocale = {
  [SupportedLocales.UK]: { name: '', description: '', original_name: '' },
  [SupportedLocales.RU]: { name: '', description: '', original_name: '' },
};

interface IProductMainInfoForm {
  handleSubmit: (values: TProductMainInfoFormValues) => void;
  isLoading: boolean;
  formInstance: IFormInstance;
  initialValues?: IProduct | null;
  handleValuesChange: () => void;
  productId?: string;
}

const ProductMainInfoForm = ({
  handleSubmit,
  isLoading,
  formInstance,
  initialValues,
  handleValuesChange,
  productId,
}: IProductMainInfoForm) => {
  const { t } = useTranslation(['products', 'common']);

  const rules = useProductDetailsValidationRules();
  const placeholder = t('common_placeholder_input', { ns: 'common' });
  const transformedInitialValues = useTransformedProductInitialValues(initialValues);

  const unitCode = useWatchForm<MeasureUnitCode | undefined>(ProductMainInfoFormFieldsNames.UNIT_CODE, formInstance);
  const showPackageFields = !isServiceUnit(unitCode);
  const packageQuantityLabel = getPackageQuantityLabel(unitCode, t);
  const isIntegerQuantity = isIntegerUnit(unitCode);
  const fieldSources = transformedInitialValues?.field_sources;
  const descriptionSourceUk = getFieldSourceLabel(t, fieldSources?.description?.[SupportedLocales.UK]);
  const descriptionSourceRu = getFieldSourceLabel(t, fieldSources?.description?.[SupportedLocales.RU]);

  const handleTranslateField = useCallback(
    async (field: string, text: string): Promise<string> => {
      if (!productId) return '';

      try {
        const response = await ProductsAPI.translateField({ productId, field, text });
        return response.data.data.translated;
      } catch {
        showErrorToast({ message: t('product_translate_field_error') });
        return '';
      }
    },
    [productId, t],
  );

  useEffect(() => {
    if (transformedInitialValues) {
      formInstance.setFieldsValue(transformedInitialValues);
    }
  }, [transformedInitialValues, formInstance]);

  if (isLoading) return <Skeleton />;

  return (
    <StyledForm
      initialValues={transformedInitialValues ?? { locale: initialLocale }}
      onFinish={handleSubmit}
      form={formInstance}
      onValuesChange={handleValuesChange}
    >
      <FormItem style={{ width: '100%' }} name={LOCALES_FIELD_NAME}>
        <LocalesFormFields
          labelPrefix={'main_info_product_'}
          ns={'products'}
          textAreaFields={['description']}
          withoutRulesFields={['original_name']}
          rules={rules[ProductMainInfoFormFieldsNames.LOCALE]}
          translateFields={['original_name', 'description']}
          onTranslate={productId ? handleTranslateField : undefined}
          fieldLabelSuffixes={{
            description: {
              [SupportedLocales.UK]: descriptionSourceUk ?? undefined,
              [SupportedLocales.RU]: descriptionSourceRu ?? undefined,
            },
          }}
        />
      </FormItem>
      <FormItem
        name={ProductMainInfoFormFieldsNames.SLUG}
        label={t('main_info_product_slug_label')}
        rules={rules[ProductMainInfoFormFieldsNames.SLUG]}
      >
        <Input placeholder={placeholder} />
      </FormItem>
      <StyledFormItemsBlock>
        <FormItem
          name={ProductMainInfoFormFieldsNames.EXTERNAL_ID}
          label={t('products_list_partner_sku')}
          rules={rules[ProductMainInfoFormFieldsNames.EXTERNAL_ID]}
        >
          <Input placeholder={placeholder} />
        </FormItem>

        <FormItem name={ProductMainInfoFormFieldsNames.SKU} label={t('products_list_sku')}>
          <Input disabled placeholder={placeholder} />
        </FormItem>
      </StyledFormItemsBlock>

      <StyledFormItemsBlock>
        <FormItem name={ProductMainInfoFormFieldsNames.ID} label={t('main_info_product_id_label')}>
          <Input placeholder={placeholder} disabled />
        </FormItem>

        <FormItem
          name={ProductMainInfoFormFieldsNames.UKTZED}
          label={withFieldSourceLabel(t, 'main_info_product_uksd_label', fieldSources?.uktzed)}
          rules={rules[ProductMainInfoFormFieldsNames.UKTZED]}
        >
          <Input />
        </FormItem>
      </StyledFormItemsBlock>

      <StyledFormItemsBlock>
        <FormItem
          name={ProductMainInfoFormFieldsNames.WEIGHT}
          label={withFieldSourceLabel(t, 'main_info_product_weight', fieldSources?.weight)}
          rules={rules[ProductMainInfoFormFieldsNames.WEIGHT]}
        >
          <InputNumber />
        </FormItem>

        <FormItem
          name={ProductMainInfoFormFieldsNames.LENGTH}
          label={withFieldSourceLabel(t, 'main_info_product_length', fieldSources?.length)}
          rules={rules[ProductMainInfoFormFieldsNames.LENGTH]}
        >
          <InputNumber />
        </FormItem>
      </StyledFormItemsBlock>

      <StyledFormItemsBlock>
        <FormItem
          name={ProductMainInfoFormFieldsNames.HEIGHT}
          label={withFieldSourceLabel(t, 'main_info_product_height', fieldSources?.height)}
          rules={rules[ProductMainInfoFormFieldsNames.HEIGHT]}
        >
          <InputNumber />
        </FormItem>

        <FormItem
          name={ProductMainInfoFormFieldsNames.WIDTH}
          label={withFieldSourceLabel(t, 'main_info_product_width', fieldSources?.width)}
          rules={rules[ProductMainInfoFormFieldsNames.WIDTH]}
        >
          <InputNumber />
        </FormItem>
      </StyledFormItemsBlock>

      <FormItem
        label={t('product_unit_name_label')}
        name={ProductMainInfoFormFieldsNames.UNIT_CODE}
        rules={rules[ProductMainInfoFormFieldsNames.UNIT_CODE]}
      >
        <StyledUnitsSelect options={getUnitsOptions(t)} />
      </FormItem>

      {showPackageFields && (
        <StyledFormItemsBlock>
          <FormItem name={ProductMainInfoFormFieldsNames.PACKAGE_QUANTITY} label={packageQuantityLabel}>
            <InputNumber
              min={isIntegerQuantity ? 1 : 0.0001}
              step={isIntegerQuantity ? 1 : 0.01}
              precision={isIntegerQuantity ? 0 : 2}
              style={{ width: '100%' }}
            />
          </FormItem>

          <FormItem name={ProductMainInfoFormFieldsNames.MIN_ORDER_PACKAGES} label={t('min_order_packages_label')}>
            <InputNumber min={1} step={1} precision={0} style={{ width: '100%' }} />
          </FormItem>
        </StyledFormItemsBlock>
      )}

      <StyledFormItemsBlock>
        <FormItem
          name={ProductMainInfoFormFieldsNames.IMPORTED}
          label={t('main_info_product_imported_label')}
          rules={rules[ProductMainInfoFormFieldsNames.IMPORTED]}
        >
          <StyledRadioGroup>
            <Radio value={true}>{t('regions_and_prices_yes_title')}</Radio>
            <Radio value={false}>{t('regions_and_prices_non_title')}</Radio>
          </StyledRadioGroup>
        </FormItem>

        <FormItem
          name={ProductMainInfoFormFieldsNames.IS_CUSTOM_ORDER}
          label={t('main_info_product_is_custom_order_label')}
          rules={rules[ProductMainInfoFormFieldsNames.IS_CUSTOM_ORDER]}
        >
          <StyledRadioGroup>
            <Radio value={true}>{t('regions_and_prices_yes_title')}</Radio>
            <Radio value={false}>{t('regions_and_prices_non_title')}</Radio>
          </StyledRadioGroup>
        </FormItem>
      </StyledFormItemsBlock>
      <StyledDivider />
      <ProductPhotosField
        formInstance={formInstance}
        initialPhotos={transformedInitialValues?.photos}
        handleValuesChange={handleValuesChange}
      />
    </StyledForm>
  );
};

export default ProductMainInfoForm;
